import { useEffect, useRef, useState } from 'react';
import { readStored, removeStored, storedKeys, writeStored } from '@/components/desktop';

/**
 * Half-typed forms, kept on the machine.
 *
 * A shipment with twenty lines is ten minutes of somebody's evening. Before
 * this, a reload, a crash, a power cut or a stray Ctrl+W threw it away with no
 * warning. Now it comes back.
 *
 * Built on the storage helpers the rest of the app already uses, so keys are
 * scoped per person (`poultry.u7.draft.…`) and a private-mode browser that
 * refuses to store anything degrades quietly instead of breaking the form.
 *
 * What is deliberately *not* here: anything that reaches the server. A draft
 * is typing, not a saved record. Posting while offline is a separate and much
 * larger question — see `.docs/012-offline-and-drafts.md`.
 */

const PREFIX = 'poultry.draft.';

/** The pause before a draft is written, matching the preferences store. */
export const DRAFT_SAVE_MS = 400;

/** Drafts older than this are swept at sign-in. */
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60_000;

interface StoredDraft<T> {
  savedAt: number;
  data: T;
}

function keyFor(name: string): string {
  return `${PREFIX}${name}`;
}

/** Reads a draft, or null when there is none worth restoring. */
export function readDraft<T>(name: string): { savedAt: Date; data: T } | null {
  const stored = readStored<StoredDraft<T> | null>(keyFor(name), null);
  if (!stored || typeof stored.savedAt !== 'number' || stored.data === undefined) return null;
  if (Date.now() - stored.savedAt > DRAFT_MAX_AGE_MS) {
    removeStored(keyFor(name));
    return null;
  }
  return { savedAt: new Date(stored.savedAt), data: stored.data };
}

export function writeDraft<T>(name: string, data: T): void {
  writeStored(keyFor(name), { savedAt: Date.now(), data } satisfies StoredDraft<T>);
}

export function forgetDraft(name: string): void {
  removeStored(keyFor(name));
}

/**
 * Throws away drafts nobody came back to. Called at sign-in, when the person's
 * own keys are readable, so a week of abandoned half-forms does not sit in
 * storage forever and eventually fill it.
 */
export function sweepOldDrafts(): void {
  for (const key of storedKeys([PREFIX])) {
    const stored = readStored<StoredDraft<unknown> | null>(key, null);
    if (!stored || typeof stored.savedAt !== 'number') {
      removeStored(key);
    } else if (Date.now() - stored.savedAt > DRAFT_MAX_AGE_MS) {
      removeStored(key);
    }
  }
}

export interface DraftHandle {
  /** When the restored draft was written, or null if nothing was restored. */
  restoredAt: Date | null;
  /** Throws the draft away — for "Start again", and after a successful save. */
  forget: () => void;
}

/**
 * Keeps one form's values, and gives back whatever was there last time.
 *
 * ```ts
 * const draft = useDraft('inward.new', { header, lines }, (saved) => {
 *   setHeader(saved.header);
 *   setLines(saved.lines);
 * });
 * ```
 *
 * `restore` runs once, on mount, before the first save. `current` is watched
 * after that and written on a short debounce, so typing a line does not mean a
 * write per keystroke.
 *
 * `name` must identify the form *and* what it is editing — `inward.new` and
 * `inward.edit.42` are different drafts, or editing one document would offer
 * you another one's typing.
 */
export function useDraft<T>(
  name: string,
  current: T,
  restore: (saved: T) => void,
  options: { enabled?: boolean } = {},
): DraftHandle {
  const enabled = options.enabled ?? true;
  const [restoredAt, setRestoredAt] = useState<Date | null>(null);
  // Nothing is written until the draft has been looked for, or the empty form
  // rendered on mount would overwrite what we are about to restore.
  const ready = useRef(false);
  const latest = useRef(current);
  latest.current = current;

  useEffect(() => {
    if (!enabled) return;
    const found = readDraft<T>(name);
    if (found) {
      restore(found.data);
      setRestoredAt(found.savedAt);
    }
    ready.current = true;
    return () => {
      ready.current = false;
      setRestoredAt(null);
    };
    // Only the key matters: `restore` closes over this render's setters, and
    // re-running on every render would restore the draft over the typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, enabled]);

  useEffect(() => {
    if (!enabled || !ready.current) return;
    const timer = setTimeout(() => writeDraft(name, latest.current), DRAFT_SAVE_MS);
    return () => clearTimeout(timer);
  }, [name, current, enabled]);

  return {
    restoredAt,
    forget: () => {
      forgetDraft(name);
      setRestoredAt(null);
    },
  };
}
